<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import SourceView from './SourceView.vue'
import HtmlFrame from './HtmlFrame.vue'
import type { EngineId, Job } from '@/core/types'
import { copyText, downloadText, withExtension } from '@/core/files'
import { toHtmlDocument } from '@/core/render'

const props = defineProps<{
  job: Job | null
  markdown: string
  html: string
  htmlSource: string
}>()

type Tab = 'preview' | 'markdown' | 'html'
type Mode = 'source' | 'preview'

const tab = ref<Tab>('preview')
// Each tab remembers its own Source/Preview choice, so switching tabs does not
// silently change what the other one shows.
const modes = ref<Record<'markdown' | 'html', Mode>>({ markdown: 'source', html: 'source' })
const copied = ref(false)

const ENGINE_LABELS: Record<EngineId, string> = {
  anydoc: 'anydoc',
  odl: 'layout engine',
}

const TABS: { id: Tab; label: string }[] = [
  { id: 'preview', label: 'Preview' },
  { id: 'markdown', label: 'Markdown' },
  { id: 'html', label: 'HTML' },
]

const hasToggle = computed(() => tab.value !== 'preview')
const mode = computed(() => (tab.value === 'preview' ? 'preview' : modes.value[tab.value]))

/** What the copy and download buttons act on for the current tab. */
const payload = computed(() =>
  tab.value === 'html'
    ? {
        text: toHtmlDocument(props.html, props.job?.name ?? 'document'),
        ext: 'html',
        type: 'text/html',
      }
    : { text: props.markdown, ext: 'md', type: 'text/markdown' },
)

async function copy(): Promise<void> {
  copied.value = await copyText(payload.value.text)
  if (copied.value) setTimeout(() => (copied.value = false), 1600)
}

function download(): void {
  if (!props.job) return
  downloadText(
    withExtension(props.job.name, payload.value.ext),
    payload.value.text,
    payload.value.type,
  )
}

// A fresh document should open on the rendered view, not on whatever raw pane
// was left selected from the previous file.
watch(
  () => props.job?.id,
  () => (tab.value = 'preview'),
)
</script>

<template>
  <section
    class="flex h-full min-h-0 flex-col rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
  >
    <header
      class="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2 dark:border-slate-800"
    >
      <div class="flex gap-1" role="tablist" aria-label="Output format">
        <button
          v-for="item in TABS"
          :key="item.id"
          type="button"
          role="tab"
          :aria-selected="tab === item.id"
          class="rounded-lg px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
          :class="
            tab === item.id
              ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
              : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
          "
          @click="tab = item.id"
        >
          {{ item.label }}
        </button>
      </div>

      <div
        v-if="hasToggle && tab !== 'preview'"
        class="flex gap-0.5 rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800"
        role="group"
        aria-label="View mode"
      >
        <button
          v-for="option in ['source', 'preview'] as Mode[]"
          :key="option"
          type="button"
          :aria-pressed="modes[tab] === option"
          class="rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
          :class="
            modes[tab] === option
              ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100'
              : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
          "
          @click="modes[tab] = option"
        >
          {{ option }}
        </button>
      </div>

      <div class="ml-auto flex items-center gap-1.5">
        <!-- Which engine actually ran. Worth surfacing: a PDF can fall back
             from the layout engine to anydoc, and that explains the output. -->
        <p
          v-if="job?.result"
          class="hidden pr-1 text-xs text-slate-500 sm:block dark:text-slate-400"
        >
          {{ ENGINE_LABELS[job.result.engine] }} · {{ Math.round(job.result.durationMs) }} ms
        </p>
        <button
          type="button"
          class="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-slate-100 disabled:opacity-40 dark:border-slate-700 dark:hover:bg-slate-800"
          :disabled="!job || job.status !== 'done'"
          @click="copy"
        >
          {{ copied ? 'Copied' : 'Copy' }}
        </button>
        <button
          type="button"
          class="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-slate-100 disabled:opacity-40 dark:border-slate-700 dark:hover:bg-slate-800"
          :disabled="!job || job.status !== 'done'"
          @click="download"
        >
          Download .{{ payload.ext }}
        </button>
      </div>
    </header>

    <div class="min-h-0 flex-1 overflow-hidden">
      <!-- Nothing selected yet. -->
      <p v-if="!job" class="grid h-full place-items-center p-8 text-center text-sm text-slate-500">
        Add a document to see its Markdown and HTML.
      </p>

      <!-- Failure: show the reason and, where there is one, the way out. -->
      <div v-else-if="job.status === 'error'" class="grid h-full place-items-center p-8">
        <div class="max-w-md text-center">
          <p class="text-sm font-medium text-rose-600 dark:text-rose-400">
            {{ job.error?.message }}
          </p>
          <p v-if="job.error?.hint" class="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {{ job.error.hint }}
          </p>
        </div>
      </div>

      <div v-else-if="job.status !== 'done'" class="grid h-full place-items-center p-8">
        <p class="animate-pulse text-sm text-slate-500">Converting {{ job.name }}…</p>
      </div>

      <!-- eslint-disable vue/no-v-html -- sanitised in core/render.ts via DOMPurify -->
      <div
        v-else-if="mode === 'preview' && tab !== 'html'"
        class="doc-preview h-full overflow-auto p-6"
        v-html="html"
      />
      <!-- eslint-enable vue/no-v-html -->

      <HtmlFrame v-else-if="mode === 'preview'" :html="html" :title="job.name" />

      <SourceView
        v-else
        :text="tab === 'html' ? htmlSource : markdown"
        :label="tab === 'html' ? 'HTML source' : 'Markdown source'"
      />
    </div>
  </section>
</template>
